import { useParams } from "react-router-dom";
import { Check } from "lucide-react";
import { useSeo } from "@/lib/useSeo";
import { mediaUrl, useApi } from "@/lib/api";
import { Btn, PageHero, Reveal, SectionHeading, Spinner } from "@/components/site/kit";
import { DynIcon } from "@/components/site/DynIcon";
import { ProductGrid } from "@/components/site/ProductCard";
import { TestimonialSlider } from "@/components/site/TestimonialSlider";
import { FaqList } from "@/components/site/FaqList";
import { LeadForm } from "@/components/site/LeadForm";
import { CtaBanner } from "@/components/site/Sections";
import NotFound from "@/pages/NotFound";

const Wrap = ({ children, alt }) => <section className={`section ${alt ? "bg-page" : "bg-white"}`}><div className="container-x">{children}</div></section>;

export const BLOCKS = {
  hero: (d) => (
    <PageHero badge={d.badge} title={d.title} highlight={d.highlight} subtitle={d.subtitle}>
      {d.button_text && <Btn to={d.button_link || "/contact"} className="mt-8" testId="block-hero-btn">{d.button_text}</Btn>}
    </PageHero>
  ),
  rich_text: (d, alt) => <Wrap alt={alt}><div className="legal-content mx-auto max-w-3xl text-base md:text-lg" dangerouslySetInnerHTML={{ __html: d.html }} /></Wrap>,
  image: (d, alt) => <Wrap alt={alt}><figure><img src={mediaUrl(d.url)} alt={d.alt || ""} loading="lazy" className="w-full rounded-2xl border border-line" />{d.caption && <figcaption className="mt-3 text-center text-sm text-muted2">{d.caption}</figcaption>}</figure></Wrap>,
  features: (d, alt) => (
    <Wrap alt={alt}>
      {d.title && <SectionHeading title={d.title} highlight={d.highlight} subtitle={d.subtitle} />}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {(d.items || []).map((it, i) => (
          <Reveal key={i} delay={(i % 3) * 0.08}><div className="card-base card-hover h-full p-7"><span className="grid h-12 w-12 place-items-center rounded-xl bg-[#EEF6FC] text-ocean"><DynIcon name={it.icon} className="h-6 w-6" /></span><h3 className="mt-5 text-lg font-bold">{it.title}</h3><p className="mt-2 text-sm text-body">{it.description}</p></div></Reveal>
        ))}
      </div>
    </Wrap>
  ),
  product_cards: (d, alt) => <Wrap alt={alt}>{d.title && <SectionHeading title={d.title} highlight={d.highlight} />}<ProductGrid limit={d.limit} /></Wrap>,
  testimonials: (d, alt) => <Wrap alt={alt}>{d.title && <SectionHeading title={d.title} center />}<TestimonialSlider /></Wrap>,
  faq: (d, alt) => <Wrap alt={alt}>{d.title && <SectionHeading title={d.title} />}<div className="max-w-3xl"><FaqList category={d.category || "home"} testId="block-faq" /></div></Wrap>,
  cta: (d, alt) => <Wrap alt={alt}><CtaBanner title={d.title} subtitle={d.subtitle} button={d.button_text} to={d.button_link || "/contact"} /></Wrap>,
  contact_form: (d, alt) => <Wrap alt={alt}><div className="card-base mx-auto max-w-2xl p-6 sm:p-9">{d.title && <h2 className="mb-6 text-2xl font-bold">{d.title}</h2>}<LeadForm source={d.source || "dynamic page"} testPrefix="block-lead-form" /></div></Wrap>,
  custom_html: (d, alt) => <Wrap alt={alt}><div dangerouslySetInnerHTML={{ __html: d.html }} /></Wrap>,
  list: (d, alt) => <Wrap alt={alt}><ul className="space-y-2">{(d.items || []).map((x) => <li key={x} className="flex gap-2"><Check className="h-4 w-4 text-success" />{x}</li>)}</ul></Wrap>,
};

export default function DynamicPage() {
  const { slug } = useParams();
  const { data: p, isLoading, isError } = useApi(`/pages/${slug}`);
  useSeo({ title: p?.seo_title || p?.title, description: p?.seo_description, image: p?.og_image });
  if (isLoading) return <Spinner className="min-h-[60vh]" />;
  if (isError || !p) return <NotFound />;
  const blocks = p.blocks || [];
  return (
    <div data-testid="dynamic-page">
      {blocks[0]?.type !== "hero" && <PageHero title={p.title} />}
      {blocks.map((b, i) => <div key={i} data-testid={`block-${b.type}-${i}`}>{BLOCKS[b.type]?.(b.data || {}, i % 2 === 1)}</div>)}
    </div>
  );
}
